import { useEffect, useSyncExternalStore, type ReactNode } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { Emoji } from './Emoji';
import { cn } from './util';

export type ToastTone = 'info' | 'success' | 'error' | 'warning' | 'blue' | 'red';

export interface ToastItem {
  id: number;
  title: ReactNode;
  body?: ReactNode;
  emoji?: string;
  tone: ToastTone;
  /** ms before auto-dismiss; 0 = sticky. */
  duration: number;
}

export type ToastInput = Omit<ToastItem, 'id' | 'tone' | 'duration'> &
  Partial<Pick<ToastItem, 'tone' | 'duration'>>;

type Listener = () => void;

/** Minimal external store: no context needed, callable from anywhere (`toast({...})`). */
function createToastStore() {
  let items: ToastItem[] = [];
  let nextId = 1;
  const listeners = new Set<Listener>();
  const emit = () => listeners.forEach((l) => l());
  return {
    subscribe(l: Listener) {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    getSnapshot: () => items,
    push(input: ToastInput): number {
      const id = nextId++;
      const item: ToastItem = { tone: 'info', duration: 3800, ...input, id };
      items = [...items, item].slice(-5);
      emit();
      return id;
    },
    dismiss(id: number) {
      items = items.filter((t) => t.id !== id);
      emit();
    },
    clear() {
      items = [];
      emit();
    },
  };
}

export const toastStore = createToastStore();

/** Show a toast. Returns its id (for `toastStore.dismiss(id)`). */
export function toast(input: ToastInput | string): number {
  return toastStore.push(typeof input === 'string' ? { title: input } : input);
}

const TONE: Record<ToastTone, { cls: string; emoji: string }> = {
  info: { cls: 'bg-white', emoji: '💬' },
  success: { cls: 'bg-jury-tint', emoji: '✅' },
  error: { cls: 'bg-team-b-tint', emoji: '💥' },
  warning: { cls: 'bg-yellow', emoji: '⚠️' },
  blue: { cls: 'bg-team-a-tint', emoji: '🔵' },
  red: { cls: 'bg-team-b-tint', emoji: '🔴' },
};

function ToastCard({ item }: { item: ToastItem }) {
  useEffect(() => {
    if (!item.duration) return;
    const t = setTimeout(() => toastStore.dismiss(item.id), item.duration);
    return () => clearTimeout(t);
  }, [item.id, item.duration]);
  const tone = TONE[item.tone];
  return (
    <motion.li
      layout
      initial={{ x: 120, opacity: 0, rotate: 6 }}
      animate={{ x: 0, opacity: 1, rotate: -1 }}
      exit={{ x: 140, opacity: 0, rotate: 8, transition: { duration: 0.18 } }}
      transition={{ type: 'spring', stiffness: 480, damping: 26 }}
      className={cn(
        'pointer-events-auto flex w-80 items-start gap-3 border-3 border-ink px-3 py-2.5 shadow-ink',
        tone.cls,
      )}
    >
      <Emoji emoji={item.emoji ?? tone.emoji} size={26} label="" className="mt-0.5" />
      <div className="min-w-0 flex-1">
        <div className="comic text-[19px] leading-tight">{item.title}</div>
        {item.body && (
          <div className="mt-0.5 text-[13px] leading-snug font-bold text-ink-soft">{item.body}</div>
        )}
      </div>
      <button
        type="button"
        aria-label="Bildirimi kapat"
        onClick={() => toastStore.dismiss(item.id)}
        className="comic -mt-0.5 -mr-1 px-1 text-[18px] text-ink/60 hover:text-ink"
      >
        ✕
      </button>
    </motion.li>
  );
}

/** Mount once near the app root. */
export function ToastHost({ className }: { className?: string }) {
  const items = useSyncExternalStore(
    toastStore.subscribe,
    toastStore.getSnapshot,
    toastStore.getSnapshot,
  );
  return (
    <ol
      aria-live="polite"
      aria-label="Bildirimler"
      className={cn(
        'pointer-events-none fixed right-4 bottom-4 z-[120] flex flex-col items-end gap-3',
        className,
      )}
    >
      <AnimatePresence initial={false}>
        {items.map((t) => (
          <ToastCard key={t.id} item={t} />
        ))}
      </AnimatePresence>
    </ol>
  );
}
